import { useState, type ChangeEvent } from 'react';
import { useImportUsersCsv } from '../../hooks/use-admin';
import { ApiError } from '../../lib/api-client';

const SAMPLE_CSV = `firstName,lastName,email
Max,Mustermann,max@example.com
Lara,Lerche,lara@example.com`;

export function AdminCsvImportPage() {
  const [csvText, setCsvText] = useState('');
  const importMut = useImportUsersCsv();

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result ?? ''));
    reader.readAsText(f);
  };

  const err =
    importMut.error instanceof ApiError ? importMut.error.message : null;
  const res = importMut.data;

  return (
    <div className="px-4 py-4 space-y-4">
      <h1 className="text-xl font-semibold">CSV-Mitgliederimport</h1>

      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-2">
        <p className="text-xs text-slate-400">
          Format: <code className="text-slate-300">firstName,lastName,email</code>
          {' '}als erste Zeile. Bestehende User (außer Status INVITED) werden
          übersprungen.
        </p>
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
            Beispiel
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-slate-950 p-2 text-[11px] text-slate-300">
            {SAMPLE_CSV}
          </pre>
        </details>
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-2">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-sm file:text-slate-100"
        />
        <textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder="…oder hier einfügen"
          rows={10}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs"
        />
        <button
          onClick={() => importMut.mutate(csvText)}
          disabled={!csvText.trim() || importMut.isPending}
          className="w-full rounded-lg bg-brand-600 px-4 py-2.5 font-medium hover:bg-brand-700 disabled:opacity-60"
        >
          {importMut.isPending ? 'Importiert …' : 'Importieren'}
        </button>
        {err && <p className="text-sm text-rose-400">{err}</p>}
      </section>

      {res && (
        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-2">
          <p className="text-sm font-medium">Ergebnis</p>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <Stat label="Importiert" value={res.imported} cls="text-emerald-400" />
            <Stat label="Übersprungen" value={res.skipped} cls="text-amber-400" />
            <Stat label="Fehlerhaft" value={res.failed} cls="text-rose-400" />
          </div>
          {res.errors.length > 0 && (
            <details open className="text-xs">
              <summary className="cursor-pointer text-slate-400">
                Fehler ({res.errors.length})
              </summary>
              <ul className="mt-2 space-y-1">
                {res.errors.map((e, i) => (
                  <li
                    key={i}
                    className="rounded border border-rose-900 bg-rose-950/30 p-2 text-rose-200"
                  >
                    Zeile {e.row}
                    {e.email ? ` · ${e.email}` : ''}: {e.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  cls,
}: {
  label: string;
  value: number;
  cls: string;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950 p-2">
      <p className={`text-2xl font-bold tabular-nums ${cls}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </p>
    </div>
  );
}
